import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  motion,
  AnimatePresence,
  LayoutGroup,
  useReducedMotion,
  useMotionValue,
  useTransform,
  useSpring,
  useScroll,
} from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  RotateCw,
  Grid,
  Star,
  CheckCircle,
  X as CloseIcon,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  Circle,
  Dot,
} from "lucide-react";

// Image imports
import img16 from "@/assets/img/16.jpg";
import img26 from "@/assets/img/26.jpg";
import img25 from "@/assets/img/25.jpg";
import img37 from "@/assets/img/37.jpg";
import img58 from "@/assets/img/58.jpg";
import img61 from "@/assets/img/61.jpg";
import img15 from "@/assets/img/15.jpg";
import img19 from "@/assets/img/19.jpg";
import img32 from "@/assets/img/32.jpg";
import img49 from "@/assets/img/49.jpg";
import img60 from "@/assets/img/60.jpg";
import img67 from "@/assets/img/67.jpg";
import img64 from "@/assets/img/64.jpg";
import img68 from "@/assets/img/1.jpeg";
import img69 from "@/assets/img/2.jpeg";
import img70 from "@/assets/img/3.jpeg";
import img66 from "@/assets/img/75.jpg";
import img65 from "@/assets/img/74.jpg";
import img71 from "@/assets/img/76.jpg";
import img56 from "@/assets/img/56.jpg";

// *** NEW *** Hero Slider Image Imports
import heroImg1 from "@/assets/images/1.jpg";
import heroImg2 from "@/assets/images/2.jpg";
import heroImg3 from "@/assets/images/3.jpg";


// Data
const artistStories = [
  {
    name: "Layla Hassan",
    image: img68,
    bio: "A visual storyteller exploring identity and culture through digital art.",
  },
  {
    name: "Omar Khattab",
    image: img70,
    bio: "From graffiti to gallery walls, Omar transforms city energy into fine art.",
  },
  {
    name: "Noura Salem",
    image: img69,
    bio: "Her work blends dreams and nature in soft, vibrant palettes.",
  },
];

const galleryItems = [
  { image: img37 },
  { image: img61 },
  { image: img32 },
  { image: img56 },
];

const voicesData = [
  {
    name: "Sara Nabil",
    role: "Digital Artist",
    message:
      "This platform gave me the space to express myself like never before.",
    image: img68,
  },
  {
    name: "Kareem Mostafa",
    role: "Art Collector",
    message: "The immersive views made me feel like I was inside the studio.",
    image: img70,
  },
  {
    name: "Aya Helmy",
    role: "Gallery Visitor",
    message: "Loved the detail grid layout and how everything felt alive.",
    image: img69,
  },
];

const behindScenesData = [
  {
    title: "Studio Vibes",
    image: img66,
    caption: "Late-night sketching sessions full of music and ideas.",
  },
  {
    title: "Painting in Progress",
    image: img65,
    caption: "Layer by layer, every brushstroke tells a story.",
  },
  {
    title: "Digital Workstation",
    image: img71,
    caption: "From concept to final render — the magic behind the screen.",
  },
];

const portfolioItemsData = [
  {
    id: "p01",
    category: "pop",
    title: "Vibrant Pop",
    image: img16,
    link: "portfolio/pop-art",
    featured: false,
    year: 2023,
    accentColor: "var(--wine-red-dark-mode-accent-primary)",
    medium: "Digital Painting",
    dimensions: "1200x1600px",
    alt: "A vibrant explosion of colors in a pop art style, depicting a stylized portrait.",
  },
  {
    id: "p02",
    category: "monochrome",
    title: "Deep Monochrome",
    image: img26,
    link: "portfolio/black-and-white",
    featured: false,
    year: 2022,
    accentColor: "var(--content-text-secondary)",
    medium: "Charcoal on Canvas",
    dimensions: "80x100cm",
    alt: "A high-contrast monochrome piece exploring textures and shadows.",
  },
  {
    id: "p03",
    category: "photorealism",
    title: "Hyper Real",
    image: img25,
    link: "portfolio/photorealizm",
    featured: false,
    year: 2023,
    accentColor: "var(--wine-red-dark-mode-accent-secondary)",
    medium: "Oil on Panel",
    dimensions: "50x70cm",
    alt: "An incredibly detailed photorealistic painting that challenges perception.",
  },
  {
    id: "p04",
    category: "drawings",
    title: "Expressive Lines",
    image: img37,
    link: "portfolio/drawings",
    featured: false,
    year: 2021,
    accentColor: "var(--content-link-color)",
    medium: "Ink on Paper",
    dimensions: "A3",
    alt: "A dynamic drawing characterized by its fluid and expressive linework.",
  },
  {
    id: "p05",
    category: "sculpture",
    title: "Tactile Forms",
    image: img58,
    link: "portfolio/sculptures",
    featured: true,
    year: 2022,
    accentColor: "var(--wine-red-dark-mode-accent-primary)",
    medium: "Bronze",
    dimensions: "45x30x20cm",
    alt: "A bronze sculpture with a focus on texture and organic forms.",
  },
  {
    id: "p06",
    category: "scifi",
    title: "Future Scapes",
    image: img61,
    link: "portfolio/sci-fi",
    featured: false,
    year: 2023,
    accentColor: "var(--wine-red-dark-mode-accent-secondary)",
    medium: "3D Render",
    dimensions: "3840x2160px",
    alt: "A breathtaking science fiction landscape depicting an alien world.",
  },
  {
    id: "p07",
    category: "pop",
    title: "Iconic Pop",
    image: img15,
    link: "portfolio/pop-art",
    featured: true,
    year: 2022,
    accentColor: "var(--wine-red-dark-mode-accent-primary)",
    medium: "Acrylic on Canvas",
    dimensions: "100x100cm",
    alt: "A bold pop art piece playing with iconic imagery and bright colors.",
  },
  {
    id: "p08",
    category: "photorealism",
    title: "Captured Detail",
    image: img19,
    link: "portfolio/photorealizm",
    featured: false,
    year: 2021,
    accentColor: "var(--wine-red-dark-mode-accent-secondary)",
    medium: "Graphite on Paper",
    dimensions: "60x90cm",
    alt: "A photorealistic drawing showcasing meticulous attention to detail.",
  },
  {
    id: "p09",
    category: "monochrome",
    title: "Shadow & Light",
    image: img32,
    link: "portfolio/black-and-white",
    featured: false,
    year: 2023,
    accentColor: "var(--content-text-secondary)",
    medium: "Digital Monochrome",
    dimensions: "1920x1080px",
    alt: "A study of shadow and light in a stark monochrome palette.",
  },
  {
    id: "p10",
    category: "drawings",
    title: "Graphite Stories",
    image: img49,
    link: "portfolio/drawings",
    featured: true,
    year: 2022,
    accentColor: "var(--content-link-color)",
    medium: "Pencil Sketch",
    dimensions: "A4",
    alt: "A series of graphite sketches telling a subtle narrative.",
  },
  {
    id: "p11",
    category: "sculpture",
    title: "Shaped Reality",
    image: img60,
    link: "portfolio/sculptures",
    featured: false,
    year: 2021,
    accentColor: "var(--wine-red-dark-mode-accent-primary)",
    medium: "Mixed Media",
    dimensions: "Varies",
    alt: "An abstract sculpture that plays with form and material to shape reality.",
  },
  {
    id: "p12",
    category: "scifi",
    title: "Cosmic Visions",
    image: img67,
    link: "portfolio/sci-fi",
    featured: false,
    year: 2023,
    accentColor: "var(--wine-red-dark-mode-accent-secondary)",
    medium: "Digital Art",
    dimensions: "2000x3000px",
    alt: "A vibrant and imaginative vision of cosmic phenomena and distant galaxies.",
  },
];
const portfolioFilters = [
  { id: "*", label: "Cosmos", icon: <RotateCw size={18} /> },
  { id: "pop", label: "Pop Nova", icon: <Star size={18} /> },
  {
    id: "photorealism",
    label: "Reality Shard",
    icon: <CheckCircle size={18} />,
  },
  { id: "monochrome", label: "Mono Nebula", icon: <Grid size={18} /> },
  { id: "drawings", label: "Graphite Galaxy", icon: <Grid size={18} /> },
  { id: "sculpture", label: "Form Singularity", icon: <Grid size={18} /> },
  { id: "scifi", label: "Astro Punks", icon: <Grid size={18} /> },
];
const featuredCollectionsData = [
  {
    title: "Nova Pop Chronicles",
    description: "Groundbreaking iterations in pop.",
    image: img67,
    link: "portfolio/pop-art",
    accentColor: "var(--wine-red-dark-mode-accent-primary)",
  },
  {
    title: "Celestial Cartography",
    description: "Mapping the far reaches of Sci-Fi.",
    image: img64,
    link: "portfolio/sci-fi",
    accentColor: "var(--wine-red-dark-mode-accent-secondary)",
  },
];


// *** NEW *** Hero Slider Data
const heroSliderData = [
    {
        image: heroImg1,
        title: "Art Through Time & Space",
        subtitle: "A curated collection of visual journeys.",
    },
    {
        image: heroImg2,
        title: "Visions of the Future",
        subtitle: "Exploring digital realms and cosmic wonders.",
    },
    {
        image: heroImg3,
        title: "The Essence of Form",
        subtitle: "Where traditional mediums meet modern expression.",
    },
];

// --- SUB-COMPONENT DEFINITIONS ---

// *** NEW *** Immersive Hero Slider Component
const ImmersiveHeroSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % heroSliderData.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + heroSliderData.length) % heroSliderData.length);
    };
    
    // Autoplay functionality
    useEffect(() => {
        const timer = setInterval(() => {
            nextSlide();
        }, 7000); // Change slide every 7 seconds
        return () => clearInterval(timer);
    }, []);

    const slide = heroSliderData[currentSlide];

    return (
        <header className="ap-immersive-hero-slider">
            <AnimatePresence initial={false} mode="wait">
                <motion.div
                    key={currentSlide}
                    className="ap-hero-slide-background"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1, transition: { duration: 1.5, ease: [0.4, 0.0, 0.2, 1] } }}
                    exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeOut" } }}
                >
                    <motion.div 
                        className="ap-hero-slide-image"
                        style={{ backgroundImage: `url(${slide.image})` }}
                        // Ken Burns Effect
                        initial={{ scale: 1, x: 0, y: 0 }}
                        animate={{ scale: 1.15, x: '2%', y: '-1%', transition: { duration: 15, ease: "linear" } }}
                    />
                </motion.div>
            </AnimatePresence>

            <div className="ap-hero-slide-overlay"></div>

            <div className="ap-hero-slide-content">
                <motion.h1
                    key={`${currentSlide}-title`}
                    className="ap-main-title pg-title-main-text"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 1, ease: "easeOut" } }}
                >
                    {slide.title.split(" ").map((word, index) => (
                      <span key={index} className={["Time", "Space", "Future", "Form"].includes(word) ? "pg-title-accent-text" : ""}>
                        {word}{' '}
                      </span>
                    ))}
                </motion.h1>
                <motion.p
                    key={`${currentSlide}-subtitle`}
                    className="ap-hero-subtitle"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0, transition: { delay: 0.8, duration: 0.8, ease: "easeOut" } }}
                >
                    {slide.subtitle}
                </motion.p>
            </div>
            
            {/* Navigation Arrows */}
            <motion.button 
                className="ap-hero-nav prev" 
                onClick={prevSlide}
                whileHover={{ scale: 1.1, x: -5 }}
                whileTap={{ scale: 0.95 }}
            >
                <ChevronLeft size={36} />
            </motion.button>
            <motion.button 
                className="ap-hero-nav next" 
                onClick={nextSlide}
                whileHover={{ scale: 1.1, x: 5 }}
                whileTap={{ scale: 0.95 }}
            >
                <ChevronRightIcon size={36} />
            </motion.button>

            {/* Dot Indicators */}
            <div className="ap-hero-dots">
                {heroSliderData.map((_, index) => (
                    <button key={index} onClick={() => setCurrentSlide(index)} className={`ap-hero-dot ${currentSlide === index ? 'active' : ''}`}>
                        {currentSlide === index ? <Circle size={10} fill="currentColor" /> : <Circle size={8} />}
                    </button>
                ))}
            </div>
        </header>
    );
};


const ConstellationFilter = ({ filters, activeFilter, onFilterChange }) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <motion.div className="ap-constellation-filter-container">
      {filters.map((filter, index) => (
        <motion.button
          key={filter.id}
          className={`ap-constellation-filter-shard ${
            activeFilter === filter.id ? "active" : ""
          }`}
          onClick={() => onFilterChange(filter.id)}
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              type: "spring",
              stiffness: 100,
              damping: 12,
              delay: index * 0.08,
            },
          }}
          whileHover={
            shouldReduceMotion
              ? {}
              : {
                  scale: 1.1,
                  y: -3,
                  boxShadow: `0 0 25px -5px var(--filter-item-hover-accent, var(--color-accent-glow))`,
                  zIndex: 10,
                }
          }
          style={{
            "--filter-item-accent":
              activeFilter === filter.id
                ? "var(--color-accent-primary)"
                : "var(--color-text-secondary)",
            "--filter-item-hover-accent":
              activeFilter === filter.id
                ? "var(--color-accent-primary)"
                : "var(--color-accent-secondary)",
          }}
        >
          {" "}
          {filter.icon} <span className="ap-shard-label">{filter.label}</span>
          {activeFilter === filter.id && (
            <motion.div
              className="ap-shard-active-glow"
              layoutId="activeShardGlow"
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            />
          )}
        </motion.button>
      ))}
    </motion.div>
  );
};

// ArtworkOrb updated to handle layoutType for moving circles
const ArtworkOrb = ({ item, layoutType, onClick }) => {
  const shouldReduceMotion = useReducedMotion();
  const isOverview = layoutType === "overview";

  const overviewDrift =
    shouldReduceMotion || !isOverview
      ? {}
      : {
          x: [0, Math.random() * 8 - 4, Math.random() * 8 - 4, 0],
          y: [0, Math.random() * 8 - 4, Math.random() * 8 - 4, 0],
          transition: {
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "linear",
          },
        };

  return (
    <motion.div
      layout
      className={`ap-artwork-orb ${
        isOverview ? "overview-particle" : "detail-orb"
      }`}
      onClick={() => onClick(item)} // Clickable in both modes
      initial={{ opacity: 0, scale: 0.6, filter: "blur(8px)" }}
      animate={{
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        transition: { type: "spring", damping: 15, stiffness: 100, mass: 0.8 },
        ...overviewDrift,
      }}
      exit={{
        opacity: 0,
        scale: 0.6,
        filter: "blur(8px)",
        transition: { duration: 0.3, ease: "easeIn" },
      }}
      whileHover={
        shouldReduceMotion
          ? {}
          : {
              scale: isOverview ? 1.15 : 1.05, // Slightly larger hover for particles
              boxShadow: `0 8px 40px -10px ${
                item.accentColor || "var(--color-shadow-primary)"
              }`,
              y: isOverview ? -2 : -5,
            }
      }
      style={{
        "--orb-accent-color": item.accentColor || "var(--content-link-color)",
      }}
    >
      <motion.img
        src={item.image}
        alt={item.title}
        className="ap-orb-image"
        layoutId={`art-image-${item.id}`} // layoutId should be unique
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.1 } }}
      />
      {/* Text info and featured icon only shown in detailGrid mode */}
      {!isOverview && (
        <>
          <motion.div
            className="ap-orb-info"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.25 } }}
          >
            <h4 className="ap-orb-title">{item.title}</h4>{" "}
            <p className="ap-orb-year">{item.year}</p>
          </motion.div>
          {item.featured && (
            <Star
              size={16}
              strokeWidth={1.5}
              className="ap-orb-featured-icon"
            />
          )}
        </>
      )}
    </motion.div>
  );
};

const ImmersiveDetailView = ({ artwork, onClose, onNavigate }) => {
  return (
    <motion.div
      className="ap-immersive-backdrop"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { delay: 0.3, duration: 0.3 } }}
    >
      <motion.div
        className="ap-immersive-content bg-card"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.85, y: 50 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: "spring",
            stiffness: 180,
            damping: 25,
            delay: 0.1,
          },
        }}
        exit={{
          opacity: 0,
          scale: 0.85,
          y: 30,
          transition: { duration: 0.25, ease: "easeIn" },
        }}
      >
        <motion.img
          src={artwork.image}
          alt={artwork.title}
          className="ap-immersive-image"
          layoutId={`art-image-${artwork.id}`}
          transition={{ type: "spring", stiffness: 200, damping: 30 }}
        />
        <div className="ap-immersive-details text-color-paragraph">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.25, ease: "easeOut" },
            }}
            className="text-3xl font-bold text-color-heading mb-3"
          >
            {artwork.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.3, ease: "easeOut" },
            }}
            className="mb-1"
          >
            <strong className="text-[var(--color-text-muted)]">Medium:</strong>{" "}
            {artwork.medium}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.35, ease: "easeOut" },
            }}
            className="mb-1"
          >
            <strong className="text-[var(--color-text-muted)]">
              Dimensions:
            </strong>{" "}
            {artwork.dimensions}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.4, ease: "easeOut" },
            }}
            className="mb-4"
          >
            <strong className="text-[var(--color-text-muted)]">Year:</strong>{" "}
            {artwork.year}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.45, ease: "easeOut" },
            }}
            className="text-sm leading-relaxed"
          >
            {artwork.alt}
          </motion.p>
          <motion.button
            className="mt-6 button-primary w-full py-3 focus-ring-accent"
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.5, ease: "easeOut" },
            }}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            Enquire <ExternalLink size={16} className="ml-2 inline-block" />
          </motion.button>
        </div>
        <button
          onClick={() => onNavigate(-1)}
          className="ap-immersive-nav prev"
        >
          <ChevronLeft size={32} />
        </button>
        <button onClick={() => onNavigate(1)} className="ap-immersive-nav next">
          <ChevronRightIcon size={32} />
        </button>
      </motion.div>
      <motion.button
        onClick={onClose}
        className="ap-immersive-close-btn"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: { delay: 0.35, type: "spring", stiffness: 150 },
        }}
        exit={{ opacity: 0, scale: 0.7 }}
      >
        <CloseIcon size={28} />
      </motion.button>
    </motion.div>
  );
};

const ArtPortfolio = () => {
  const [activeFilter, setActiveFilter] = useState("*");
  const [layoutType, setLayoutType] = useState("overview"); // Default to 'overview' for "Cosmos"
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [detailViewIndex, setDetailViewIndex] = useState(0);
  const [isDarkModeActual, setIsDarkModeActual] = useState(false); // Tracks the actual .dark class state

  // REVERSED Theme Logic: OS Light -> Site Dark, OS Dark -> Site Light
  useEffect(() => {
    const prefersLightScheme = window.matchMedia(
      "(prefers-color-scheme: light)"
    ); // Check for light preference
    const applyTheme = (isLightPreferred) => {
      document.documentElement.classList.toggle("dark", isLightPreferred); // Add 'dark' if OS is light
      setIsDarkModeActual(isLightPreferred); // isDarkModeActual is true if site is in dark mode
    };
    applyTheme(prefersLightScheme.matches);
    const handleChange = (e) => applyTheme(e.matches);
    prefersLightScheme.addEventListener("change", handleChange);
    return () => prefersLightScheme.removeEventListener("change", handleChange);
  }, []);

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setLayoutType(filterId === "*" ? "overview" : "detailGrid");
  };

  const handleArtworkClick = (item) => {
    const itemIndex = portfolioItemsData.findIndex((art) => art.id === item.id);
    setDetailViewIndex(itemIndex);
    setSelectedArtwork(item);
  };

  const handleCloseImmersiveView = () => setSelectedArtwork(null);

  const navigateImmersiveView = (direction) => {
    setDetailViewIndex((prevFullListIndex) => {
      const newFullListIndex =
        (prevFullListIndex + direction + portfolioItemsData.length) %
        portfolioItemsData.length;
      setSelectedArtwork(portfolioItemsData[newFullListIndex]);
      return newFullListIndex;
    });
  };

  const itemsToDisplay = useMemo(() => {
    if (activeFilter === "*") return portfolioItemsData;
    return portfolioItemsData.filter((item) => item.category === activeFilter);
  }, [activeFilter]);

  // Container variants with conditional staggerChildren
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: layoutType === "overview" ? 0.03 : 0.05,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <main
      role="main"
      className="ap-chronoscape-main-container portfolio-main "
    >
      {/* REVERSED inline theme detection script */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
          (function() {
            try {
              // If OS prefers light, add 'dark' class. Otherwise, remove it.
              if (window.matchMedia('(prefers-color-scheme: light)').matches) {
                document.documentElement.classList.add('dark');
              } else {
                document.documentElement.classList.remove('dark');
              }
            } catch (e) {}
          })();
        `,
        }}
      />
      
      {/* *** REPLACED ChronoscapeHero with ImmersiveHeroSlider *** */}
      <ImmersiveHeroSlider />

      <section id="ap-gallery-section" className="ap-gallery-section-wrapper">
        <ConstellationFilter
          filters={portfolioFilters}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
        />
        <LayoutGroup>
          <motion.div
            className={`ap-artworks-display-area ${
              layoutType === "overview"
                ? "mode-overview-floating"
                : "mode-detail-grid"
            }`}
            key={`${activeFilter}-${layoutType}`} // Ensure re-render on layoutType change as well
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {itemsToDisplay.map((item) => (
              <ArtworkOrb
                key={item.id}
                item={item}
                layoutType={layoutType} // Pass layoutType
                onClick={handleArtworkClick}
              />
            ))}
          </motion.div>
        </LayoutGroup>
      </section>


      <section className="ap-featured-collections-section fc-section">
        <div className="ap-section-header">
          <h2 className="fc-title-main-text">Spotlight Series</h2>
          <p className="fc-subtitle-text">
            Discover specially curated featured collections.
          </p>
        </div>
        <div className="ap-featured-grid">
          {featuredCollectionsData.map((collection, index) => (
            <motion.a
              key={collection.title}
              href={collection.link}
              className="ap-featured-card fc-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                  delay: index * 0.1,
                },
              }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{
                scale: 1.03,
                boxShadow: `0 12px 30px -8px ${
                  collection.accentColor || "var(--color-shadow-primary)"
                }`,
              }}
              style={{
                "--card-accent-color":
                  collection.accentColor || "var(--content-link-color)",
              }}
            >
              <img
                src={collection.image}
                alt={collection.title}
                className="ap-featured-card-image"
              />
              <div className="ap-featured-card-overlay">
                <h3 className="ap-featured-card-title">{collection.title}</h3>
                <p className="ap-featured-card-description">
                  {collection.description}
                </p>
                <span className="ap-featured-card-cta">
                  {" "}
                  Explore Series{" "}
                  <ArrowRight size={16} className="ml-1 inline-block" />{" "}
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      <AnimatePresence mode="wait">
        {selectedArtwork && (
          <ImmersiveDetailView
            artwork={selectedArtwork}
            onClose={handleCloseImmersiveView}
            onNavigate={navigateImmersiveView}
          />
        )}
      </AnimatePresence>

      <section className="ap-neon-gallery-section">
        <div className="neon-glow-bg"></div>
        <div className="neon-content">
          <h2 className="neon-title">Dive into the Art Dimension</h2>
          <p className="neon-subtext">
            Where creativity transcends space & logic.
          </p>

          <div className="floating-cards-gallery">
            {galleryItems.map((item, idx) => (
              <div className="neon-card" key={idx}>
                <img src={item.image} alt={`Art ${idx}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
};

export default ArtPortfolio;